// How payment methods, wallets and gateways read on every billing screen (Stripe since 2026-10-27).

/** Gateway codes billing stores → names people know. PayPal and Payflow are switched off but old payments keep them. */
export const GATEWAY_NAMES: Record<string, string> = {
  stripe: "Stripe",
  authorize_net: "Authorize.net",
  payflow: "Payflow",
  paypal: "PayPal",
  manual: "recorded by hand",
  keap: "Keap",
};

export const gatewayName = (g: string | null | undefined) => (g ? (GATEWAY_NAMES[g] ?? g.replace(/_/g, " ")) : "");

/** Stripe wallet codes → names. Apple Pay and Google Pay wrap a real card (brand and last 4 of the device number);
 *  Link, Amazon Pay and Cash App Pay hide the card, so there is no last 4. */
export const WALLET_NAMES: Record<string, string> = {
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  samsung_pay: "Samsung Pay",
  link: "Link",
  amazon_pay: "Amazon Pay",
  cashapp: "Cash App Pay",
};

/** Names billing puts in card_brand for a method with no card number behind it. */
const CARDLESS_BRANDS = new Set(["Link", "Amazon Pay", "Cash App Pay", "Bank account", "PayPal"]);

const METHOD_NAMES: Record<string, string> = { paypal: "PayPal", check: "Check", cash: "Cash", ach: "Bank transfer", wire: "Wire", card: "Card", other: "Other" };

export interface MethodParts {
  card_brand?: string | null;
  brand?: string | null;
  last4?: string | null;
  wallet?: string | null;
  method?: string | null;
  gateway?: string | null;
}

/** "Visa ending 4242", "Apple Pay (Visa ending 4242)", "Link", "Amazon Pay", "PayPal", "Check". Never "ending " with nothing after it. */
export function methodLabel(p: MethodParts): string {
  const brand = (p.card_brand ?? p.brand ?? "").trim();
  const last4 = (p.last4 ?? "").trim();
  const wallet = p.wallet ? (WALLET_NAMES[p.wallet] ?? p.wallet.replace(/_/g, " ")) : "";
  const card = last4 ? `${brand && !CARDLESS_BRANDS.has(brand) ? brand : "Card"} ending ${last4}` : "";
  if (wallet) return card && !["link", "amazon_pay", "cashapp"].includes(p.wallet ?? "") ? `${wallet} (${card})` : wallet;
  if (card) return card;
  if (brand) return brand;
  if (p.method) return METHOD_NAMES[p.method] ?? p.method.replace(/_/g, " ");
  return gatewayName(p.gateway) || "Unknown";
}

/** billing's own one-string label, made fit for staff: its customer wording "your card" (no last 4) becomes "Card",
 *  and a dangling "ending" is dropped. */
export function serverMethodLabel(label: string | null | undefined): string {
  const t = (label ?? "").trim().replace(/ ending\s*$/, "");
  if (!t) return "Unknown";
  const s = t.replace(/^your\s+/i, "");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** True when there is no card behind the method (no expiry to show or check). */
export function isCardless(p: MethodParts): boolean {
  const brand = (p.card_brand ?? p.brand ?? "").trim();
  return !(p.last4 ?? "").trim() && (CARDLESS_BRANDS.has(brand) || ["link", "amazon_pay", "cashapp"].includes(p.wallet ?? ""));
}

/** Only links into Stripe's own dashboard are shown (billing builds them with the account and test/live mode). */
export function stripeDashboardURL(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === "https:" && u.hostname === "dashboard.stripe.com" ? u.toString() : null;
  } catch {
    return null;
  }
}

/** Stripe's dispute statuses → a chip. Open ones are red; a won dispute is settled in our favor. */
export function disputeChip(status: string | null | undefined): { label: string; variant: "destructive" | "warning" | "success" | "muted" } | null {
  if (!status) return null;
  switch (status) {
    case "needs_response":
    case "warning_needs_response":
      return { label: "disputed: answer in Stripe", variant: "destructive" };
    case "under_review":
    case "warning_under_review":
      return { label: "disputed: under review", variant: "warning" };
    case "won":
      return { label: "dispute won", variant: "success" };
    case "lost":
      return { label: "dispute lost", variant: "destructive" };
    case "warning_closed":
      return { label: "dispute closed", variant: "muted" };
  }
  return { label: "disputed", variant: "destructive" };
}
